<script setup>
// Reference 7 — the price, and the two things a manager asks straight after it.
//
// The card DISPLAYS the price; it does not replace stating it. The full sentence stays
// on the page as one contiguous line beside it, because that is the line a manager
// quotes to a board — and because breaking its first clause across the card's three
// typographic slots would read fine while quietly losing the one place the page
// commits to a number in words.
import { ref, onMounted, onUnmounted } from "vue";
import { useMotion, reveal } from "@/lib/motion";

const root = ref(null);
const blocks = ref([]);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    reveal(blocks.value, { stagger: 0.1, y: 16, trigger: root.value, start: "top 80%" });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <section id="prijs" ref="root" class="border-t-2 border-foreground bg-background">
    <div class="mx-auto w-full max-w-6xl px-5 py-10 sm:py-16">
      <div class="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between sm:gap-10">
        <!-- Deliberately outside the revealed set: the sentence below is the one line a
             manager quotes to a board, and it is the last thing on this page that
             should ever be waiting on a scroll position to become readable. -->
        <div class="max-w-2xl">
          <h2 class="font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">Eén prijs, per eenheid.</h2>
          <p class="mt-3 text-sm leading-relaxed text-muted-foreground sm:mt-4 sm:text-base">
            € 1,00 per actieve eenheid per maand, exclusief 21% btw. Geen minimumbedrag en geen opstartkosten. Je ontvangt per VvE een btw-factuur voor
            die vergoeding.
          </p>
        </div>
        <div
          :ref="(el) => (blocks[0] = el)"
          class="shrink-0 border-2 border-foreground bg-card px-4 py-3 text-left shadow-[var(--shadow-hard)] sm:px-6 sm:py-4 sm:text-right"
        >
          <p class="font-mono text-3xl font-bold tabular-nums text-primary sm:text-5xl">€ 1,00</p>
          <p class="mt-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-muted-foreground sm:mt-1.5 sm:text-[0.6875rem]">
            per actieve eenheid, per maand
          </p>
          <p class="mt-2 border-t-2 border-foreground/10 pt-2 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-muted-foreground sm:mt-3 sm:pt-3 sm:text-[0.6875rem]">
            Excl. 21% btw
          </p>
        </div>
      </div>

      <div :ref="(el) => (blocks[1] = el)" class="mt-6 grid gap-4 border-t-2 border-foreground/10 pt-6 sm:mt-10 sm:grid-cols-2 sm:gap-10 sm:pt-8">
        <div>
          <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-primary sm:text-[0.6875rem]">Wat Mollie rekent</p>
          <p class="mt-1.5 text-sm leading-relaxed text-muted-foreground sm:text-base">
            De transactiekosten van de incasso's rekent Mollie rechtstreeks af met de VvE, via haar eigen Mollie-overeenkomst. Pandig zet daar geen
            opslag op en neemt geen commissie op de bijdragen.
          </p>
        </div>
        <div>
          <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-primary sm:text-[0.6875rem]">Voor een portefeuille</p>
          <p class="mt-1.5 text-sm leading-relaxed text-muted-foreground sm:text-base">
            Voor een portefeuille hebben we geen staffel of portefeuilletarief verzonnen. Neem contact op — dan kijken we samen wat past.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
